import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import SFCheckbox from "../checkbox";
import { SFArrowDownIcon, SFKeyboardArrowRightIcon } from "../icons";
import SFTypography from "../typography";
import getStyled from "../../hooks/getStyled";

import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import Collapse from "@mui/material/Collapse";
import _ from "lodash";

import { getGridFilterValues } from "../../services/dataGridService";

const { styled } = getStyled();

const CHECK_STATUS = {
  UN_CHECKED: 0,
  CHECKED: 1,
  INDETERMINATE: 2,
};

const ListItemIconStyled = styled(ListItemIcon)(({ theme }) => ({
  justifyContent: "center",
  minWidth: "40px",
}));

const ListItemButtonStyled = styled(ListItemButton)(({ theme }) => ({
  padding: 0,
}));

const SFVirtualTreeFilter = (props) => {
  //the tree structure declared in coldef , used to assign field type to each item after the request
  let treeStructure = props.colDef.treeStructure;
  //this is the name of the custom tree column
  let filterField = props.colDef.filterField;
  const [innerData, setData] = useState([]);
  //inner filter state that gets sent to the ag grid after filter changes
  const [filterState, setFilterState] = useState(null);
  const [isFirstRender, setIsFirstRender] = useState(true);

  const handleFilterChanged = () => {
    let filterModel = props.api.getFilterModel();
    let model = filterModel[props.colDef.groupFilter];
    if (model) {
      Object.keys(model.columns).forEach((k) => {
        props.api.getColumnFilterInstance(k, (filterInstance) => {
          let existingValues = filterInstance.getValues();
          filterInstance.setFilterValues(
            _.unionBy(
              existingValues,
              model.columns[k].values.map((x) => {
                return { id: x.id, value: x.value };
              }),
              "id"
            )
          );
          filterInstance.applyModel();
        });
      });
      setFilterState(model.columns);
    } else {
      setFilterState(undefined);
    }
  };

  useEffect(() => {
    props.api.addEventListener("filterChanged", handleFilterChanged);
    return () => {
      //check if api is destroyed before removing event listeners
      if (props.api?.isDestroyed() === false) {
        props.api.removeEventListener("filterChanged", handleFilterChanged);
      }
    };
  }, []);

  useEffect(() => {
    if (innerData.length === 0) {
      requestData(treeStructure.field, null);
    }
  }, [innerData]);

  useEffect(() => {
    if (filterState !== null) {
      if (isFirstRender) {
        setIsFirstRender(false);
      } else {
        //if filterState is not null then we notify the grid that the filter has changed and we refresh the data to resolve what
        //is checked or not
        let data = _.cloneDeep(innerData);
        refreshData(filterState, data);
        setData(data);

        configureAgGridModel(props.api, filterState, props.colDef.groupFilter);
      }
    }
  }, [filterState, isFirstRender]);

  //click handler for all checkboxes.Item is passed as a parameter and an array of each parent nodes for the item
  const clickHandler = (item, parentNodes) => {
    let model = {};
    //calculation of the new filterState here
    model = resolveModel(item, parentNodes, _.cloneDeep(filterState));
    setFilterState(model);
  };

  //request for children data
  const requestData = (field, parentId = null) => {
    getGridFilterValues(
      props.colDef.treeFilterValuesUrl,
      JSON.stringify({ colName: filterField, parentId: parentId })
    ).then((resp) => {
      let currData = [...innerData];
      let responseData = resp.data;
      let parent;

      if (parentId) {
        parent = findParent(parentId, currData);
        responseData.forEach((x) => {
          x.field = field;
          x.parentIds = [...parent.parentIds, parentId];
          x.checked = resolveCheck(x, filterState);
        });
        parent.children = resp.data;
      } else {
        responseData.forEach((x) => {
          x.field = field;
          x.parentIds = [];
          x.checked = resolveCheck(x, filterState);
        });
        currData = responseData;
      }
      setData(currData);
    });
  };

  return (
    <React.Fragment>
      <div>
        {innerData.length !== 0 &&
          innerData.map((item) => {
            return (
              <Branch
                key={item.id}
                item={item}
                treeStructure={treeStructure}
                requestData={requestData}
                clickHandler={clickHandler}
                data={_.cloneDeep(innerData)}
                parentNodes={[]}
              />
            );
          })}
      </div>
    </React.Fragment>
  );
};

export default SFVirtualTreeFilter;

const Branch = ({
  item,
  treeStructure,
  requestData,
  clickHandler,
  data,
  parentNodes,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState(item.checked);
  useEffect(() => {
    setChecked(item.checked);
  }, [data]);

  //decides what the Branch will render based on the tree structure declared in column definition.
  //either a nested branch or nodes
  const renderBranches = () => {
    if (treeStructure.child) {
      return item.children.map((x) => {
        return (
          <Branch
            key={x.id}
            item={x}
            treeStructure={treeStructure.child}
            requestData={requestData}
            clickHandler={clickHandler}
            data={data}
            parentNodes={parentNodes.concat(item)}
          />
        );
      });
    } else {
      return (
        item && (
          <Node
            key={item.id}
            item={item}
            clickHandler={clickHandler}
            treeStructure={treeStructure}
            data={data}
            parentNodes={parentNodes}
          />
        )
      );
    }
  };

  return (
    <>
      <List sx={{ pl: 1 }} key={item.value} component="div" disablePadding>
        {treeStructure.child && (
          <React.Fragment>
            <ListItemButton
              disableGutters
              onClick={() => {
                if (!open && !item.children) {
                  requestData(treeStructure.child.field, item.id);
                }
                setOpen((prv) => !prv);
              }}
            >
              <ListItemIconStyled>
                {open && <SFArrowDownIcon />}
                {!open && <SFKeyboardArrowRightIcon />}
              </ListItemIconStyled>
              <ListItemText>
                <SFTypography variant="body1">
                  {item.value} ({item.count})
                </SFTypography>
              </ListItemText>
            </ListItemButton>
            {open && item.children && (
              <Collapse in={open} timeout="auto" unmountOnExit>
                <ListItemButtonStyled
                  sx={{ pl: 1 }}
                  onClick={() => {
                    //we modify the item checked status here before calling the click handler.
                    //this is safe because items are copies of the state
                    item.checked =
                      item.checked === CHECK_STATUS.CHECKED
                        ? CHECK_STATUS.UN_CHECKED
                        : CHECK_STATUS.CHECKED;
                    clickHandler(item, parentNodes);
                  }}
                >
                  <ListItemIconStyled>
                    <SFCheckbox
                      checked={checked === CHECK_STATUS.CHECKED}
                      indeterminate={checked === CHECK_STATUS.INDETERMINATE}
                    />
                  </ListItemIconStyled>
                  <ListItemText>
                    <SFTypography variant="body2">
                      {t("components.datagrid.treeFilter.selectAll")}
                    </SFTypography>
                  </ListItemText>
                </ListItemButtonStyled>
                {item.children && renderBranches()}
              </Collapse>
            )}
          </React.Fragment>
        )}
        {!treeStructure.child && renderBranches()}
      </List>
    </>
  );
};

const Node = ({ item, clickHandler, data, parentNodes }) => {
  const [checked, setChecked] = useState(item.checked);

  useEffect(() => {
    setChecked(item.checked);
  }, [data]);

  return (
    <ListItemButtonStyled
      disableGutters
      onClick={() => {
        //we modify the item's check status so we can resolve the model easier
        //when resolve model is called. This is safe because although the item
        //is passed by reference in the click handler , we are using a deep
        //copy of the initial data state
        item.checked =
          item.checked === CHECK_STATUS.CHECKED
            ? CHECK_STATUS.UN_CHECKED
            : CHECK_STATUS.CHECKED;
        clickHandler(item, parentNodes);
      }}
    >
      <ListItemIconStyled>
        <SFCheckbox checked={checked === CHECK_STATUS.CHECKED}></SFCheckbox>
      </ListItemIconStyled>
      <ListItemText>
        <SFTypography variant="body1">
          {item.value} ({item.count})
        </SFTypography>
      </ListItemText>
    </ListItemButtonStyled>
  );
};

//finds the parent of the item in the tree
function findParent(parentId, data) {
  let result = data.find((x) => x.id === parentId);
  if (result) {
    return result;
  }

  for (let index = 0; index < data.length; index++) {
    if (data[index].children && !result) {
      result = findParent(parentId, data[index].children);
    }
  }

  return result;
}

//this goes through the filter state and decides the item check status
function resolveCheck(item, filterState) {
  let checked = CHECK_STATUS.UN_CHECKED;
  //if the item is found in the filter then we set it to true
  if (filterState) {
    if (
      filterState[item.field] &&
      filterState[item.field].values.find((x) => x.id === item.id)
    ) {
      checked = CHECK_STATUS.CHECKED;
    } else {
      //the item wasnt found so we need to check in the filter for parents or children
      Object.keys(filterState).forEach((k) => {
        //skip your own field ,  item is considered not checked here , so iterate
        //every filter state field till you change status or run out of fields
        if (k !== item.field && checked === CHECK_STATUS.UN_CHECKED) {
          //checking for parents in filter
          checked = filterState[k].values.some((x) =>
            item.parentIds.includes(x.id)
          )
            ? CHECK_STATUS.CHECKED
            : CHECK_STATUS.UN_CHECKED;
          //checking for children in filter if its still not checked
          if (checked === CHECK_STATUS.UN_CHECKED) {
            checked = filterState[k].values.some((x) =>
              x.parentIds.includes(item.id)
            )
              ? CHECK_STATUS.INDETERMINATE
              : CHECK_STATUS.UN_CHECKED;
          }
        }
      });
    }
  }
  return checked;
}

//calculates the new model and the items to be deleted from the gridFilterModel
function resolveModel(item, parentNodes, filterState) {
  if (!filterState) {
    filterState = {};
  }
  //first we add / remove the item based on its status in the filter state
  if (item.checked === CHECK_STATUS.CHECKED) {
    addParentToFilterState(filterState, item);
  } else {
    removeParentFromFilterState(filterState, item);
  }

  //then we iterate through the parent nodes that decides whether the parent should be checked
  //and we  modify each time the filter state.
  //this works because on each click we modify the reference of the items
  for (let index = parentNodes.length - 1; index >= 0; index--) {
    if (
      parentNodes[index].children.every(
        (x) => x.checked === CHECK_STATUS.CHECKED
      )
    ) {
      parentNodes[index].checked = CHECK_STATUS.CHECKED;
      addParentToFilterState(filterState, parentNodes[index]);
    } else if (
      parentNodes[index].children.some(
        (x) => x.checked === CHECK_STATUS.CHECKED
      )
    ) {
      parentNodes[index].checked = CHECK_STATUS.INDETERMINATE;

      addSiblingsToFilterState(
        filterState,
        parentNodes[index].children.filter(
          (x) => x.checked === CHECK_STATUS.CHECKED
        )
      );
    } else if (
      parentNodes[index].children.some(
        (x) => x.checked === CHECK_STATUS.INDETERMINATE
      )
    ) {
      parentNodes[index].checked = CHECK_STATUS.INDETERMINATE;
    }
    //default case - all children unchecked
    else {
      parentNodes[index].checked = CHECK_STATUS.UN_CHECKED;
      removeParentFromFilterState(filterState, parentNodes[index]);
    }
  }
  return _.isEmpty(filterState) ? undefined : filterState;
}

//this hust refhreshes our inner data for ui reasons to decide what checks have changed
function refreshData(filterState, data) {
  data.forEach((x) => {
    x.checked = resolveCheck(x, filterState);
    if (x.children) {
      refreshData(filterState, x.children);
    }
  });
}

//adds an item to the filter state and removes its children && parents if any
function addParentToFilterState(filterState, item) {
  const { id, value, parentIds } = item;
  if (filterState[item.field]) {
    filterState[item.field].values.find((x) => x.id === id) ??
      filterState[item.field].values.push({ id, value, parentIds });
  } else {
    filterState[item.field] = {
      filterType: "set",
      values: [{ id, value, parentIds }],
    };
  }
  //removing children
  Object.keys(filterState).forEach((k) => {
    if (filterState[k].values.some((x) => x.parentIds.includes(item.id))) {
      filterState[k].values = filterState[k].values.filter(
        (x) => !x.parentIds.includes(item.id)
      );
    }
    //delete the children key in filter state if no values exist
    if (filterState[k].values.length === 0) {
      delete filterState[k];
    }
  });

  //remove parents
  Object.keys(filterState).forEach((k) => {
    if (filterState[k].values.some((x) => item.parentIds.includes(x.id))) {
      filterState[k].values = filterState[k].values.filter(
        (x) => !item.parentIds.includes(x.id)
      );
    }
    //delete the children key in filter state if no values exist
    if (filterState[k].values.length === 0) {
      delete filterState[k];
    }
  });
}

//adds siblings to the filter state and removes the parent as well as the parent key
function addSiblingsToFilterState(filterState, siblings) {
  let first = siblings[0];
  let children = siblings.map((x) => {
    return { id: x.id, value: x.value, parentIds: x.parentIds };
  });
  if (filterState[first.field]) {
    filterState[first.field].values = _.unionBy(
      filterState[first.field].values,
      children,
      "id"
    );
  } else {
    filterState[first.field] = {
      filterType: "set",
      values: children,
    };
  }
  //removing parent
  Object.keys(filterState).forEach((k) => {
    if (filterState[k].values.some((x) => first.parentIds.includes(x.id))) {
      filterState[k].values = filterState[k].values.filter(
        (x) => !first.parentIds.includes(x.id)
      );
    }
    if (filterState[k].values.length === 0) {
      delete filterState[k];
    }
  });
}

//removes an item from the filter state and also removes children
function removeParentFromFilterState(filterState, parent) {
  if (filterState[parent.field]) {
    filterState[parent.field].values = filterState[parent.field].values.filter(
      (x) => x.id !== parent.id
    );
    if (filterState[parent.field].values.length === 0) {
      delete filterState[parent.field];
    }
  }

  //removing children
  Object.keys(filterState).forEach((k) => {
    if (filterState[k].values.some((x) => x.parentIds.includes(parent.id))) {
      filterState[k].values = filterState[k].values.filter(
        (x) => !x.parentIds.includes(parent.id)
      );

      if (filterState[k].values.length === 0) {
        delete filterState[k];
      }
    }
  });
}

function configureAgGridModel(api, filterState, filterKey) {
  let existingModel = _.cloneDeep(api.getFilterModel()) ?? {};

  if (!filterState) {
    delete existingModel[filterKey];
  } else {
    existingModel[filterKey] = {
      filterType: "groupTreeFilter",
      columns: filterState,
    };
  }
  api.setFilterModel(existingModel);
  api.onFilterChanged();
}
